import { useCallback, useMemo } from 'react';
import type { AppManifest } from '@api/apps';
import { SandboxRequestError } from '@components/HtmlSandboxViewer';
import { IDLE_LIVE } from '../useAppRunStream';
import { savePendingAppStart } from '../usePendingAppStart';
import { NoRun, type AppFrameController, type StartRequest } from '../appFrame/useAppRunController';
import type { SignupReason } from './SignupGateDialog';

const noRun = (): Promise<void> => Promise.reject(new NoRun());
const nothing = () => undefined;

/**
 * The controller of a visitor with no account. The page is fully usable, but
 * nothing reaches the server: Run is kept for after signup and opens the gate,
 * and there is no run to refine, answer, stop or read the transcript of.
 */
export const useGuestAppController = (
  manifest: AppManifest | null,
  onSignupRequired: (reason: SignupReason) => void,
): AppFrameController => {
  const startRun = useCallback(
    async (request: StartRequest) => {
      if (manifest) {
        // Resuming is a convenience: storage the browser refuses must not keep the gate shut.
        await savePendingAppStart(manifest.key, request).catch(() => undefined);
      }
      onSignupRequired('run');
      throw new SandboxRequestError('UNSUPPORTED', 'Create a free account to run this app.');
    },
    [manifest, onSignupRequired],
  );

  const requireAccount = useCallback(() => onSignupRequired('aiRender'), [onSignupRequired]);

  return useMemo(
    () => ({
      appKey: manifest?.key,
      manifest,
      run: null,
      readOnly: false,
      testRuns: false,
      live: IDLE_LIVE,
      isBusy: false,
      submitting: false,
      stopping: false,
      error: null,
      startRun,
      refineRun: noRun,
      answerRun: noRun,
      cancelRun: noRun,
      openTranscript: nothing,
      newRun: nothing,
      requireAccount,
    }),
    [manifest, startRun, requireAccount],
  );
};
